(() => {
  if (customElements.get('ron-brand-strip')) return;

  class RonBrandStrip extends HTMLElement {
    connectedCallback() {
      this.disconnect();
      this.viewport = this.querySelector('[data-brand-strip-viewport]');
      this.track = this.querySelector('[data-brand-strip-track]');
      this.group = this.querySelector('[data-brand-strip-original]');
      this.toggle = this.querySelector('[data-brand-strip-toggle]');
      if (!this.viewport || !this.track || !this.group || !this.toggle || this.group.children.length < 2) return;
      this.originalItems = [...this.group.children];
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.preference = matchMedia('(prefers-reduced-motion: reduce)');
      this.compact = matchMedia('(max-width: 760px)');
      this.requested = this.intent ?? this.dataset.autoRotate !== 'false';
      this.visible = false;
      this.hovered = false;
      this.allowToggleFocus = false;
      this.supported = !!this.track.animate && 'IntersectionObserver' in window && 'ResizeObserver' in window && matchMedia('(hover:hover) and (pointer:fine)').matches && !navigator.maxTouchPoints && getComputedStyle(this).direction !== 'rtl' && !window.Shopify?.designMode;
      this.toggle.addEventListener('click', () => {
        this.requested = !this.requested;
        this.intent = this.requested;
        this.allowToggleFocus = this.requested;
        this.sync();
      }, { signal });
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'touch' && matchMedia('(hover:hover)').matches) { this.hovered = true; this.sync(); }
      }, { signal });
      this.addEventListener('pointerleave', () => { this.hovered = false; this.sync(); }, { signal });
      this.addEventListener('focusin', event => {
        if (this.reordering) return;
        this.allowToggleFocus = false;
        this.sync();
        if (this.originalItems.some((item, index) => this.group.children[index] !== item)) {
          this.reordering = true;
          this.originalItems.forEach(item => this.group.append(item));
          this.viewport.scrollLeft = 0;
          event.target.focus({ preventScroll: true });
          this.reordering = false;
        }
        if (this.group.contains(event.target)) event.target.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'nearest' });
      }, { signal });
      this.addEventListener('focusout', () => queueMicrotask(() => { if (this.isConnected) this.sync(); }), { signal });
      this.preference.addEventListener('change', () => this.measure(), { signal });
      this.compact.addEventListener('change', () => this.measure(), { signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      window.addEventListener('pagehide', () => { this.visible = false; this.sync(); }, { signal });
      window.addEventListener('pageshow', event => {
        if (!event.persisted) return;
        this.visibilityObserver?.unobserve(this);
        this.visibilityObserver?.observe(this);
      }, { signal });
      if (this.supported) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          this.visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .25);
          this.sync();
        }, { threshold: [0, .25] });
        this.visibilityObserver.observe(this);
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this.viewport);
      }
      this.measure();
    }

    measure() {
      this.stop();
      if (!this.group || !this.isConnected) return;
      this.distance = this.group.getBoundingClientRect().width;
      const items = [...this.group.children];
      const step = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : this.distance;
      this.canMove = this.supported && !this.preference.matches && this.distance >= this.viewport.clientWidth + step;
      this.toggle.hidden = !this.canMove;
      this.sync();
    }

    sync() {
      if (!this.toggle) return;
      this.dataset.requested = String(this.requested);
      this.toggle.querySelector('[data-brand-strip-label]').textContent = this.requested ? this.toggle.dataset.pause : this.toggle.dataset.resume;
      this.toggle.setAttribute('aria-label', this.requested ? this.toggle.dataset.pauseLabel : this.toggle.dataset.resumeLabel);
      const focusInside = this.contains(document.activeElement);
      const canRun = this.canMove && this.requested && this.visible && !this.hovered && !document.hidden && !this.preference.matches && (!focusInside || this.allowToggleFocus && document.activeElement === this.toggle);
      if (canRun) this.start();
      else this.stop();
    }

    start() {
      if (this.animation || !this.distance) return;
      this.clone?.remove();
      this.clone = this.group.cloneNode(true);
      this.clone.removeAttribute('data-brand-strip-original');
      this.clone.setAttribute('data-brand-strip-clone', '');
      this.clone.setAttribute('aria-hidden', 'true');
      this.clone.inert = true;
      this.clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
      this.clone.querySelectorAll('a').forEach(node => node.tabIndex = -1);
      this.track.append(this.clone);
      const offset = this.viewport.scrollLeft;
      this.viewport.scrollLeft = 0;
      this.duration = this.distance / (this.compact.matches ? 14 : 18) * 1000;
      this.animation = this.track.animate([{ transform: 'translateX(0)' }, { transform: `translateX(-${this.distance}px)` }], { duration: this.duration, iterations: Infinity, easing: 'linear' });
      this.animation.currentTime = offset / this.distance * this.duration;
      this.dataset.moving = 'true';
    }

    stop() {
      if (!this.animation) { this.dataset.moving = 'false'; return; }
      const offset = ((Number(this.animation.currentTime) % this.duration) / this.duration * this.distance + this.viewport.scrollLeft) % this.distance;
      this.animation.cancel();
      this.animation = null;
      const focused = this.group.contains(document.activeElement) ? document.activeElement : null;
      const items = [...this.group.children];
      const start = items[0]?.offsetLeft || 0;
      let index = 0;
      for (let i = 1; i < items.length; i++) if (items[i].offsetLeft - start <= offset) index = i;
      const residual = offset - (items[index]?.offsetLeft - start || 0);
      this.reordering = true;
      for (let i = 0; i < index; i++) this.group.append(items[i]);
      this.clone?.remove();
      this.clone = null;
      this.dataset.moving = 'false';
      this.viewport.scrollLeft = Math.max(0, residual);
      if (focused) focused.focus({ preventScroll: true });
      this.reordering = false;
    }

    disconnect() {
      this.stop();
      this.controller?.abort();
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.clone?.remove();
      this.clone = null;
      if (this.toggle) this.toggle.hidden = true;
    }

    disconnectedCallback() { this.disconnect(); }
  }

  customElements.define('ron-brand-strip', RonBrandStrip);
})();
